import { Observable } from 'rxjs';
import {
  AiwynPaymentMethod,
  BillingDetails,
  CancelPaymentPayload,
  ChargePayload,
  PaymentActionRequirement,
  PaymentMethodType,
  PaymentPlatform,
  PaymentPlatformBillingDetails,
  PaymentResult,
} from '@aiwyn/api';
import { AbstractPaymentMethod } from './payment-platform-payment-method.models';
import { LoadableStatus } from '@aiwyn/shared';
import { PaymentPlatformPaymentData } from './payment-platform-payment-data.model';
import { PaymentMethodMode } from './payment-method-metadata.models';

export class PaymentMethodDeletionBlockedError extends Error {
  constructor(
    public readonly isUsedByAutomaticPayments: boolean,
    public readonly isUsedByEngagements: boolean,
    public readonly isUsedByPendingPayments: boolean,
  ) {
    super('Payment method cannot be deleted');
  }
}

export interface AbstractPaymentPlatformService {
  /**
   * metadata
   */
  get platform(): Observable<PaymentPlatform>;
  initialize(): Observable<unknown>;
  reset(): Observable<unknown>;

  /**
   * Emits when the platform's client-side SDK transitions to a failed state — currently used by
   * the add-payment-method dialog to fire `PAYMENT_METHOD_ERROR` with stage `platform-init`.
   * Stripe's `loadStripe()` rejection (ad-blocker / CSP / network) is the canonical case. The
   * stream emits one value per transition into ERROR; subscribers should `take(1)` if they only
   * want to react once per session.
   */
  getPlatformInitErrors(): Observable<{ message: string }>;

  /**
   * payment methods
   */
  getNewPaymentMethodTypeOptions(): Observable<PaymentMethodType[]>;

  getSavedPaymentMethodsStatus(): Observable<LoadableStatus>;
  getSavedPaymentMethods(forceRefresh?: boolean): Observable<AbstractPaymentMethod[]>;
  getSavedPaymentMethodsExist(): Observable<boolean>;
  getSavedPaymentMethod(paymentMethodId: string): Observable<AbstractPaymentMethod>;

  getDefaultPaymentMethodIdStatus(): Observable<LoadableStatus>;
  getDefaultPaymentMethodId(): Observable<string>;

  createPaymentMethod(): Observable<AbstractPaymentMethod>;
  onValidPaymentMethodCreation(createdPaymentMethod: AbstractPaymentMethod): Observable<unknown>;
  onInvalidPaymentMethodCreation(error: Error): Observable<unknown>;
  savePaymentMethod(paymentMethodId: string): Observable<string>;

  setTemporaryPaymentMethod(paymentMethod: AiwynPaymentMethod): Observable<void>;
  removeTemporaryPaymentMethod(): Observable<unknown>;
  getTemporaryPaymentMethod(): Observable<AbstractPaymentMethod>;

  setSelectedSavedPaymentMethodId(paymentMethodId: string): Observable<void>;
  getSelectedPaymentMethodId(): Observable<string>;
  getSelectedSavedPaymentMethodId(): Observable<string>;

  setDefaultPaymentMethod(paymentMethodId: string): Observable<void>;
  deletePaymentMethod(paymentMethodId: string): Observable<unknown>;
  updatePaymentMethod(paymentMethodId: string, billingDetails: BillingDetails): Observable<string>;

  /**
   * payment method details
   */
  setPaymentMethodMode(paymentMethodMode: PaymentMethodMode): Observable<void>;
  getPaymentMethodMode(): Observable<PaymentMethodMode>;

  setNewPaymentMethodType(paymentMethodType: PaymentMethodType): Observable<void>;
  getNewPaymentMethodType(): Observable<PaymentMethodType>;

  setNewBillingDetails(billingDetails: BillingDetails): Observable<void>;
  removeNewBillingDetails(): Observable<void>;
  getNewBillingDetails(): Observable<BillingDetails>;

  getNewPaymentMethodFormComplete(): Observable<boolean>;

  /**
   * payments
   */
  setSelectedBusinessEntityId(businessEntityId: number): Observable<void>;
  getSelectedBusinessEntityId(): Observable<number>;
  getAdditionalPaymentData(): Observable<PaymentPlatformPaymentData>;
  makePayment(chargePayload: ChargePayload): Observable<PaymentResult>;
  handlePaymentActionRequirement(
    paymentActionRequirement: PaymentActionRequirement,
    paymentMethodType: PaymentMethodType,
  ): Observable<Error>;
  cancelPayment(cancelPaymentPayload: CancelPaymentPayload): Observable<unknown>;

  /**
   * miscellaneous
   */
  marshalError(error: any): Error;
  marshalBillingDetails(billingDetails: PaymentPlatformBillingDetails): BillingDetails;
  unmarshalBillingDetails(billingDetails: BillingDetails): PaymentPlatformBillingDetails;
}
